import React, { useState, useCallback } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ImageIcon from "@mui/icons-material/Image";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RatingStars from "./RatingStars";
import InquiryModal from "./InquiryModal";
import useInquiry from "./useInquiry";

// Utility functions
const usdFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const stripHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return div.textContent || div.innerText || '';
};

// Main Product Card Component - Optimized for responsive design
const ProductCard = ({ product, onQuickView }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    inquiryOpen,
    snackbar,
    handleInquiryOpen,
    handleInquiryClose,
    handleInquirySubmit,
    handleSnackbarClose
  } = useInquiry();

  const [isLoading, setIsLoading] = useState(false);

  const handleQuickViewClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      onQuickView(product);
    }, 300);
  }, [product, onQuickView]);

  const handleInquiryClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleInquiryOpen();
  }, [handleInquiryOpen]);

  return (
    <>
      <Card sx={{
        borderRadius: { xs: 2, sm: 3 },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-4px)' },
          boxShadow: { xs: '0 2px 8px rgba(0,0,0,0.1)', sm: '0 12px 24px rgba(0,0,0,0.15)' }
        }
      }}>
        {/* Image section - Responsive aspect ratio */}
        <Box sx={{
          position: "relative",
          pt: { xs: "75%", sm: "70%", md: "75%" },
          bgcolor: "grey.100",
          overflow: 'hidden'
        }}>
          {product.images?.length ? (
            <CardMedia
              component="img"
              image={product.images[0]}
              alt={product.name}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.4s ease',
                '&:hover': {
                  transform: { xs: 'none', sm: 'scale(1.05)' }
                }
              }}
              onError={(e) => {
                e.currentTarget.src = 'https://placehold.co/400x300/f5f5f5/999999?text=No+Image';
              }}
            />
          ) : (
            <Box sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'grey.100'
            }}>
              <ImageIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'grey.400' }} />
            </Box>
          )}

          {/* Sale Badge */}
          {product.compareAtUsd && product.priceUsd && product.compareAtUsd > product.priceUsd &&
            <Chip
              label="SALE"
              color="error"
              size="small"
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                left: { xs: 8, sm: 12 },
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                height: { xs: 20, sm: 24 },
                fontWeight: 700,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            />
          }

          {/* Favorite Button */}
          <IconButton
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 12 },
              right: { xs: 8, sm: 12 },
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
                color: 'error.main',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <FavoriteIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
          </IconButton>
        </Box>

        {/* Content section - Optimized spacing */}
        <CardContent sx={{
          flex: 1,
          p: { xs: 1.5, sm: 2 },
          pb: { xs: 1, sm: 1.5 },
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Brand and Title */}
          <Box mb={{ xs: 1, sm: 1.5 }}>
            <Typography variant="caption" color="primary" sx={{
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              lineHeight: 1
            }}>
              {product.brand}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 600,
                lineHeight: { xs: 1.3, sm: 1.4 },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mt: 0.25,
                color: 'text.primary',
                minHeight: { xs: '2.3em', sm: '2.8em' }
              }}
              title={product.name}
            >
              {product.name}
            </Typography>
          </Box>

          {/* Rating and Category */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 1, sm: 1.5 }}>
            <RatingStars value={4.5} size={isMobile ? "small" : "medium"} />
            <Chip
              label={product.category}
              size="small"
              variant="outlined"
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                height: { xs: 18, sm: 20 },
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: { xs: 0.75, sm: 1 }
                }
              }}
            />
          </Box>

          {/* Description - Fixed height for alignment */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              height: { xs: '3em', sm: '3.6em' },
              overflow: "hidden",
              display: '-webkit-box',
              WebkitLineClamp: { xs: 2, sm: 3 },
              WebkitBoxOrient: 'vertical',
              mb: { xs: 1.5, sm: 2 },
              lineHeight: 1.5,
              flex: 1
            }}
          >
            {stripHtml(product.descriptionHtml)}
          </Typography>

          {/* Price Section */}
          <Box sx={{ mt: 'auto' }}>
            {product.priceUsd != null ? (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: 700,
                    color: 'primary.main',
                    lineHeight: 1.2
                  }}
                >
                  {usdFmt.format(product.priceUsd)}
                </Typography>
                {product.compareAtUsd && product.compareAtUsd > product.priceUsd &&
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textDecoration: "line-through",
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      mt: -0.25
                    }}
                  >
                    {usdFmt.format(product.compareAtUsd)}
                  </Typography>
                }
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{
                fontSize: { xs: '0.875rem', sm: '0.95rem' },
                fontWeight: 500
              }}>
                Price on request
              </Typography>
            )}
          </Box>
        </CardContent>

        {/* Actions section - Optimized button sizing */}
        <CardActions sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          px: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 2 },
          pt: 0,
          gap: { xs: 0.75, sm: 1 }
        }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleQuickViewClick}
            disabled={isLoading}
            fullWidth
            startIcon={!isLoading ? <VisibilityIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> : null}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              py: { xs: 1, sm: 1.125 },
              borderRadius: { xs: 1.5, sm: 2 },
              fontWeight: 600,
              minHeight: { xs: 36, sm: 40 },
              borderWidth: { xs: 1, sm: 2 },
              '&:hover': {
                borderWidth: { xs: 1, sm: 2 },
                transform: { xs: 'none', sm: 'translateY(-1px)' }
              },
              transition: 'all 0.2s ease'
            }}
          >
            {isLoading ? <CircularProgress size={14} /> : 'View Details'}
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleInquiryClick}
            fullWidth
            startIcon={<ShoppingBagIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              py: { xs: 1, sm: 1.125 },
              borderRadius: { xs: 1.5, sm: 2 },
              fontWeight: 600,
              minHeight: { xs: 36, sm: 40 },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: { xs: 'none', sm: 'translateY(-1px)' }
              },
              transition: 'all 0.2s ease'
            }}
          >
            Send Inquiry
          </Button>
        </CardActions>
      </Card>

      <InquiryModal
        open={inquiryOpen}
        onClose={handleInquiryClose}
        product={product}
        onSubmit={handleInquirySubmit}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductCard;